import React from 'react';
import { Box, Typography, Skeleton } from '@mui/material';
import ViewStreamIcon from '@mui/icons-material/ViewStream';
import LinearScaleIcon from '@mui/icons-material/LinearScale';
import dayjs from 'dayjs';
import { EmployeeAvatar } from '../../../../common/EmployeeAvatar';
import { REVIEW_TYPES } from '../../../../../constants/reviewTypes';

// Component for empty/skeleton StepLabel content
export const ReviewerEmptyStepLabel = () => {
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
      {/* Skeleton for avatar */}
      <Skeleton variant="circular" width={32} height={32} animation={false} />
      <Box sx={{ flex: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {/* Skeleton for name */}
          <Skeleton variant="text" width={120} height={20} animation={false} />
          {/* Skeleton for review type badge */}
          <Skeleton 
            variant="rounded" 
            width={80} 
            height={24} 
            sx={{ borderRadius: '4px' }}
            animation={false}
          />
        </Box>
        {/* Skeleton for status text */}
        <Skeleton variant="text" width={180} height={16} animation={false} />
      </Box>
    </Box>
  );
};

// Component for empty/skeleton StepContent content
export const ReviewerEmptyStepContent = () => {
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
      {/* Skeleton for timing */}
      <Skeleton variant="text" width={150} height={16} animation={false} />
      {/* Skeleton for dates */}
      <Skeleton variant="text" width={220} height={16} animation={false} />
    </Box>
  );
};

// New component for review type badge
export const ReviewTypeBadge = ({ reviewType, getReviewTypeColor, getReviewTypeTextColor, getReviewTypeText }) => {
  return (
    <Box 
      sx={{ 
        display: 'flex', 
        alignItems: 'center',
        gap: 0.5,
        backgroundColor: getReviewTypeColor(reviewType),
        color: getReviewTypeTextColor(reviewType),
        padding: '2px 8px',
        borderRadius: '4px',
        fontSize: '12px'
      }}
    >
      {reviewType === REVIEW_TYPES.PARALLEL ? (
        <ViewStreamIcon sx={{ fontSize: 14 }} />
      ) : (
        <LinearScaleIcon sx={{ fontSize: 14 }} />
      )}
      <Typography variant="caption" sx={{ fontWeight: 500 }}>
        {getReviewTypeText(reviewType)}
      </Typography>
    </Box>
  );
};

// Component for StepLabel content
export const ReviewerStepLabel = ({ 
  reviewer, 
  getReviewTypeColor, 
  getReviewTypeTextColor, 
  getReviewTypeText, 
  getReviewerStatusText, 
  getTemplateTypeText 
}) => {
  const reviewType = reviewer.reviewType || REVIEW_TYPES.SEQUENTIAL; // Default to sequential if not specified

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
      <EmployeeAvatar employee={reviewer.employee} size={32} />
      <Box sx={{ flex: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography variant="body2" sx={{ fontWeight: 500 }}>
            {reviewer.employee.fullName}
          </Typography>
          <ReviewTypeBadge 
            reviewType={reviewType}
            getReviewTypeColor={getReviewTypeColor}
            getReviewTypeTextColor={getReviewTypeTextColor}
            getReviewTypeText={getReviewTypeText}
          />
        </Box>
        <Typography variant="caption" color="text.secondary">
          {getReviewerStatusText(reviewer)}
          {reviewer.reviewType === REVIEW_TYPES.PARALLEL ?
            ` • ${getTemplateTypeText(reviewer.skillsMatrixTemplate.type)}` :
            reviewer.order === 1 ? ' • Self Assessment' : ` • Reviewer ${reviewer.order}`
          }
        </Typography>
      </Box>
    </Box>
  );
};

// Component for StepContent content
export const ReviewerStepContent = ({ reviewer, timing }) => {
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
      {timing && (
        <Typography variant="caption" sx={{ 
          color: 'text.secondary',
          fontWeight: 500 
        }}>
          {timing}
        </Typography>
      )}
      <Typography variant="caption" color="text.secondary">
        {reviewer.startedAt && `Started: ${dayjs(reviewer.startedAt).format('MMM D, YYYY')}`}
        {reviewer.completedAt && ` • Completed: ${dayjs(reviewer.completedAt).format('MMM D, YYYY')}`}
      </Typography>
    </Box>
  );
}; 